import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";

import {
  deleteAppNotificationFromFirestore,
  listAppNotificationsFromFirestore,
  listNotificationRecipientsFromFirestore,
  sendAppNotificationToFirestore,
} from "data/firebase/notificationsApi";
import { confirmAdminAction } from "admin/contentUtils";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";

const ACTION_OPTIONS = [
  { value: "none", label: "Sadece bilgi" },
  { value: "home", label: "Keşfet" },
  { value: "deals", label: "Fırsatlar" },
  { value: "kadekon", label: "KADEKON" },
];

export default function AdminAppNotifications() {
  const [items, setItems] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [action, setAction] = useState("none");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [notifications, recipients] = await Promise.all([
        listAppNotificationsFromFirestore(),
        listNotificationRecipientsFromFirestore(),
      ]);
      setItems(notifications);
      setRecipientCount(recipients.length);
    } catch (loadError) {
      setError(loadError?.message || "Bildirimler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSend = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await sendAppNotificationToFirestore({ title, body, action, createdByName: "Admin panel" });
      setTitle("");
      setBody("");
      setAction("none");
      setFlash("Bildirim üyelere gönderildi. Mobil gelen kutusu anlık güncellenir.");
      await load();
    } catch (sendError) {
      setError(sendError?.message || "Gönderilemedi");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item) => {
    if (!confirmAdminAction(item.title || item.id, "silinsin mi?")) return;
    try {
      await deleteAppNotificationFromFirestore(item.id);
      setFlash("Bildirim silindi.");
      await load();
    } catch (deleteError) {
      setError(deleteError?.message || "Silinemedi");
    }
  };

  return (
    <Container className="mt-4" fluid>
      <AdminPageHeader
        title="Uygulama bildirimleri"
        subtitle="Üyelerin Bildirimlerim ekranına düşen duyuruları buradan gönderin. Firestore appNotifications koleksiyonu kullanılır."
      />
      {flash ? <Alert color="success">{flash}</Alert> : null}
      <AdminStateBlock loading={loading} error={error} />

      <Row>
        <Col lg="5">
          <Card className="shadow-sm mb-4">
            <CardHeader>
              <h3 className="mb-0">Yeni bildirim</h3>
              <small className="text-muted">{recipientCount} kayıtlı üye</small>
            </CardHeader>
            <CardBody>
              <Form onSubmit={onSend}>
                <FormGroup>
                  <Label>Başlık</Label>
                  <Input value={title} onChange={(event) => setTitle(event.target.value)} required />
                </FormGroup>
                <FormGroup>
                  <Label>Mesaj</Label>
                  <Input type="textarea" rows={4} value={body} onChange={(event) => setBody(event.target.value)} required />
                </FormGroup>
                <FormGroup>
                  <Label>Tıklanınca açılsın</Label>
                  <Input type="select" value={action} onChange={(event) => setAction(event.target.value)}>
                    {ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? "Gönderiliyor…" : "Bildirim gönder"}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
        <Col lg="7">
          <Card className="shadow-sm">
            <CardHeader>
              <h3 className="mb-0">Gönderilenler</h3>
            </CardHeader>
            <CardBody>
              {!items.length && !loading ? (
                <p className="text-muted mb-0">Henüz bildirim yok.</p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Başlık</th>
                      <th>Üye</th>
                      <th>Tarih</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.title}</strong>
                          <div className="text-muted small">{item.body}</div>
                        </td>
                        <td>{item.recipientCount ?? 0}</td>
                        <td>{item.createdAt ? new Date(item.createdAt).toLocaleString("tr-TR") : "—"}</td>
                        <td className="text-end">
                          <Button color="danger" outline size="sm" type="button" onClick={() => onDelete(item)}>
                            Sil
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
